// @wc-ignore-file

import { CR_BOARD_VIEW_KEY_PREFIX } from "@/lib/local-storage.consts";
import type { CrClassification, CrPriority } from "@shared/types/supabase.types";
import { parseClassificationSearch } from "./classification-filtering";
import { type CrSortSearchValue, parseCrSortSearch } from "./cr-sorting";
import { parsePrioritySearch } from "./priority-filtering";

/**
 * CR-140: the CR board remembers its last view (priority + type filters and
 * sort) for the current browser session, so leaving the board and coming back
 * shows the same cards. Pure and React-free.
 *
 * - Storage is sessionStorage: per tab, survives a reload, dies with the tab.
 * - Scoped per user AND per project.
 * - An empty view (no filter, default sort) removes the key, so a view the
 *   user cleared is remembered as "clear" by construction.
 * - URL params always win: the memory is only restored on a bare entry.
 * - Every storage access is guarded; a broken storage just means no memory.
 */

export interface CrBoardViewScope {
  userId: string;
  projectId: string;
}

/** The board route's validated search (every key present, possibly undefined). */
export interface CrBoardViewSearch {
  priority: CrPriority[] | undefined;
  type: CrClassification[] | undefined;
  sort: CrSortSearchValue | undefined;
}

/** TanStack Router's `beforeLoad` cause. */
export type BeforeLoadCause = "enter" | "stay" | "preload";

/** Stored views are a few dozen bytes; anything bigger was not written by us. */
const MAX_STORED_LENGTH = 2048;

function emptyView(): CrBoardViewSearch {
  return { priority: undefined, type: undefined, sort: undefined };
}

export function crBoardViewKey(scope: CrBoardViewScope): string {
  return `${CR_BOARD_VIEW_KEY_PREFIX}${scope.userId}:${scope.projectId}`;
}

export function isEmptyCrBoardView(search: Partial<CrBoardViewSearch>): boolean {
  return !search.priority?.length && !search.type?.length && !search.sort;
}

/** Re-validate anything (stored JSON included) through the route's own parsers. */
function normalise(value: unknown): CrBoardViewSearch {
  if (typeof value !== "object" || value === null) return emptyView();
  const record = value as Record<string, unknown>;
  return {
    priority: parsePrioritySearch(record.priority),
    type: parseClassificationSearch(record.type),
    sort: parseCrSortSearch(record.sort),
  };
}

/** `globalThis.sessionStorage`, or null when absent or when the accessor throws
 * (e.g. blocked site data). */
function defaultStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function readCrBoardView(
  scope: CrBoardViewScope,
  storage: Storage | null = defaultStorage(),
): CrBoardViewSearch {
  if (!storage) return emptyView();
  try {
    const raw = storage.getItem(crBoardViewKey(scope));
    if (raw === null || raw.length > MAX_STORED_LENGTH) return emptyView();
    return normalise(JSON.parse(raw));
  } catch {
    return emptyView();
  }
}

export function writeCrBoardView(
  scope: CrBoardViewScope,
  search: Partial<CrBoardViewSearch>,
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  const view = normalise(search);
  try {
    if (isEmptyCrBoardView(view)) {
      storage.removeItem(crBoardViewKey(scope));
    } else {
      storage.setItem(crBoardViewKey(scope), JSON.stringify(view));
    }
  } catch {
    // Quota or blocked storage: the board simply forgets.
  }
}

/**
 * The view to redirect to when entering the board, or undefined to stay put.
 * Only a fresh `enter` with a bare URL restores: `stay` re-runs on every
 * search change on the board (restoring there would re-apply filters the user
 * just cleared) and `preload` must never redirect.
 */
export function restoreCrBoardView({
  cause,
  search,
  scope,
  storage = defaultStorage(),
}: {
  cause: BeforeLoadCause;
  search: Partial<CrBoardViewSearch>;
  scope: CrBoardViewScope;
  storage?: Storage | null;
}): CrBoardViewSearch | undefined {
  if (cause !== "enter" || !isEmptyCrBoardView(search)) return undefined;
  const remembered = readCrBoardView(scope, storage);
  return isEmptyCrBoardView(remembered) ? undefined : remembered;
}
